import {
  Button,
  Col,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
  theme
} from "antd";
import React, { useState, useEffect } from "react";
import { MinusCircleOutlined, PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./InventoryUsage.css";

const { Text } = Typography;

const InventoryUsage = ({ inventoryList, form: parentForm }) => {
  const [selectedItems, setSelectedItems] = useState({});
  const [form] = parentForm ? [parentForm] : Form.useForm();
  const formValues = Form.useWatch("inventory", form);

  useEffect(() => {
    if (formValues) {
      const newSelectedItems = {};
      formValues?.forEach((item, index) => {
        if (item && item.item) {
          newSelectedItems[item.item] = index;
        }
      });
      setSelectedItems(newSelectedItems);
    }
  }, [formValues]);

  const getAvailableQty = itemId => {
    const inventoryItem = inventoryList?.find(item => item._id === itemId);
    return inventoryItem ? inventoryItem.total - inventoryItem.used : 0;
  };

  return parentForm ? (
    <FormContent
      form={form}
      inventoryList={inventoryList}
      getAvailableQty={getAvailableQty}
      selectedItems={selectedItems}
    />
  ) : (
    <Form form={form} name="inventory_usage" layout="vertical">
      <FormContent
        form={form}
        inventoryList={inventoryList}
        getAvailableQty={getAvailableQty}
        selectedItems={selectedItems}
      />
    </Form>
  );
};

const FormContent = ({ form, inventoryList, getAvailableQty, selectedItems }) => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();

  return (
    <Form.List name="inventory">
      {(fields, { add, remove }) => (
        <div className="inventory-list-container">
          {fields.map(({ key, name, ...restField }) => {
            const selectedItem = form.getFieldValue(["inventory", name, "item"]);
            const availableQty = selectedItem ? getAvailableQty(selectedItem) : 0;

            return (
              <div
                key={key}
                className="inventory-row-card mb-3 p-3"
                style={{
                  background: "var(--ds-surface)",
                  borderRadius: "var(--ds-radius-md)",
                  border: "1px solid var(--ds-border-soft)"
                }}
              >
                <Row gutter={[16, 16]} align="bottom">
                  <Col xs={24} sm={10} md={9}>
                    <Form.Item
                      {...restField}
                      label={t("Item Name")}
                      name={[name, "item"]}
                      rules={[{ required: true, message: t("enter", { field: "name" }) }]}
                      className="mb-0"
                    >
                      <Select
                        showSearch
                        placeholder={t("Select an item")}
                        optionFilterProp="children"
                        className="w-100"
                        options={inventoryList?.map(item => {
                          const isSelected =
                            selectedItems[item._id] !== undefined &&
                            selectedItems[item._id] !== name;
                          return {
                            label: `${item.itemName}`,
                            value: item._id,
                            disabled: isSelected
                          };
                        })}
                        onChange={() => {
                          const inventory = form.getFieldValue("inventory");
                          inventory[name].total = null;
                          form.setFieldsValue({ inventory });
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={12} sm={6} md={6}>
                    <Form.Item
                      {...restField}
                      label={
                        <Space>
                          {t("Qty")}
                          {selectedItem && (
                            <Tooltip title={`${availableQty} ${t("available in stock")}`}>
                              <InfoCircleOutlined
                                style={{
                                  color: availableQty < 5 ? token.colorError : token.colorPrimary
                                }}
                              />
                            </Tooltip>
                          )}
                        </Space>
                      }
                      name={[name, "total"]}
                      dependencies={[["inventory", name, "item"]]}
                      className="mb-0"
                      rules={[
                        { required: true, message: t("Qty required") },
                        {
                          validator: (_, value) => {
                            if (!selectedItem || value === undefined || value === null)
                              return Promise.resolve();
                            if (value > availableQty) {
                              return Promise.reject(`${t("Cannot exceed")} ${availableQty}`);
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <InputNumber className="w-100" placeholder="0" min={1} max={availableQty} />
                    </Form.Item>
                  </Col>

                  <Col xs={12} sm={6} md={6}>
                    <Form.Item
                      {...restField}
                      label={t("Min qty")}
                      name={[name, "minimumAmount"]}
                      className="mb-0"
                    >
                      <InputNumber className="w-100" placeholder={t("Alert limit")} min={0} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={2} md={3} className="text-end">
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined style={{ fontSize: "18px" }} />}
                      onClick={() => remove(name)}
                      className="d-flex align-items-center justify-content-center"
                      style={{ minHeight: 40, width: "100%" }}
                    >
                      <span className="d-sm-none ms-2">{t("Remove")}</span>
                    </Button>
                  </Col>
                </Row>
                {selectedItem && (
                  <div className="mt-2">
                    <Text
                      type={availableQty < 5 ? "danger" : "secondary"}
                      style={{ fontSize: "12px" }}
                    >
                      {t("Available stock")}: {availableQty}
                    </Text>
                  </div>
                )}
              </div>
            );
          })}

          <Form.Item className="mt-3">
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
              style={{ minHeight: 44, borderRadius: "var(--ds-radius-md)" }}
            >
              {t("Add Inventory Item")}
            </Button>
          </Form.Item>
        </div>
      )}
    </Form.List>
  );
};

export default InventoryUsage;
